import { useState, useEffect, useCallback } from "react";
import {
  FiSearch,
  FiBell as FiDumbbell,
  FiChevronDown,
  FiX,
} from "react-icons/fi";
import Modal from "../../../../tailadmin/components/ui/Modal";
import { Exercise } from "../../../../types";
import exercicies from "../../../../services/exercicies";
import utils from "../../../../helpers/utils";
import loadingImage from "../../../../assets/loading.png";

interface ExerciseSelectProps {
  professionalId: string;
  value: string;
  onChange: (name: string, id: number, exercise?: Exercise) => void;
}

export const ExerciseSelect = ({
  professionalId,
  value,
  onChange,
}: ExerciseSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchExercises = useCallback(async () => {
    setLoading(true);
    try {
      const data = await exercicies.list_all(professionalId, 1, 30, query);
      setResults(data);
    } catch (error) {
      console.error("Erro ao buscar exercícios:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [professionalId, query]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isOpen) {
        fetchExercises();
      }
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [isOpen, query, fetchExercises]);

  const handleSelect = (ex: Exercise) => {
    onChange(ex.name, Number(ex.id), ex);
    setIsOpen(false);
    setQuery("");
  };

  const handleClose = () => {
    setIsOpen(false);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange("", 0);
  };

  const placeholder = "Selecionar exercício";
  const hasValue = value.length > 0;

  const getEmptyMessage = () => {
    if (query.length > 0) return "Nenhum exercício encontrado";
    return "Digite para buscar exercícios";
  };

  const getMediaUrl = (ex: Exercise) => {
    if (ex.image_url && ex.image_url.trim() !== "") {
      return ex.image_url;
    }
    if (ex.image && ex.image.trim() !== "") {
      return ex.image;
    }
    return utils.getUriImage(ex.image ?? loadingImage, "all_view");
  };

  const getVideoUrl = (ex: Exercise) => {
    if (!ex.video) return null;
    if (ex.video.startsWith("data:video")) {
      return ex.video;
    }
    if (ex.video.trim() !== "") {
      return utils.getUriImage(
        ex.video,
        ex.professional_id ? String(ex.professional_id) : "all_view",
      );
    }
    return null;
  };

  const isVideoUrl = (url: string | null): boolean => {
    if (!url) return false;
    if (url.startsWith("data:video")) return true;
    const videoExtensions = [
      ".mp4",
      ".webm",
      ".ogg",
      ".mov",
      ".m4v",
      ".avi",
      ".mkv",
    ];
    return videoExtensions.some((ext) => url.toLowerCase().includes(ext));
  };

  const renderExerciseMedia = (ex: Exercise) => {
    const videoUrl = getVideoUrl(ex);
    const imageUrl = getMediaUrl(ex);
    const hasVideo = isVideoUrl(videoUrl);

    if (hasVideo) {
      return (
        <video
          src={videoUrl!}
          className="h-full w-full object-cover"
          muted
          playsInline
          loop
          autoPlay
          poster={imageUrl}
        />
      );
    }
    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt={ex.name}
          className="h-full w-full object-cover"
        />
      );
    }
    return <FiDumbbell className="h-6 w-6 text-gray-400" />;
  };

  const renderListContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Buscando exercícios...
          </span>
        </div>
      );
    }

    if (results.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
            <FiDumbbell className="h-7 w-7 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {getEmptyMessage()}
          </p>
          {query.length === 0 && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
              Use a barra de pesquisa acima
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {results.map((ex) => (
          <button
            key={ex.id}
            type="button"
            onClick={() => handleSelect(ex)}
            className="group flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-primary/5 active:bg-primary/10 dark:hover:bg-primary/10 dark:active:bg-primary/20 sm:gap-4 sm:px-5 sm:py-4"
          >
            {/* Thumbnail do exercício */}
            <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800 sm:h-16 sm:w-16">
              {renderExerciseMedia(ex)}
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            </div>

            {/* Nome do exercício */}
            <div className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-gray-900 dark:text-white sm:text-base">
                {ex.name}
              </span>
              {ex.muscle_group && (
                <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">
                  {ex.muscle_group}
                </span>
              )}
            </div>

            {/* Indicador de seleção */}
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 transition-all group-hover:border-primary dark:border-gray-600 dark:group-hover:border-primary">
              <div className="h-2 w-2 scale-0 rounded-full bg-primary transition-transform group-hover:scale-100" />
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="relative flex w-full items-center gap-2">
      {/* Botão de seleção */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
        className="group flex flex-1 items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-left text-sm font-medium outline-none transition-all hover:border-primary/50 focus:border-primary focus:ring-2 focus:ring-primary/20 active:scale-[0.99] dark:border-gray-600 dark:bg-gray-700 dark:hover:border-primary/50 dark:focus:border-primary sm:px-4"
      >
        <span
          className={`flex-1 truncate ${
            hasValue
              ? "text-gray-900 dark:text-white"
              : "text-gray-500 dark:text-gray-400"
          }`}
        >
          {value || placeholder}
        </span>
        <FiChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition-all duration-200 group-hover:text-primary ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Botão limpar */}
      {hasValue && (
        <button
          type="button"
          onClick={handleClear}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-all hover:bg-red-50 hover:text-red-500 active:scale-95 dark:hover:bg-red-900/20 dark:hover:text-red-400"
          title="Limpar seleção"
          aria-label="Limpar seleção"
        >
          <FiX className="h-5 w-5" />
        </button>
      )}

      {/* Modal de seleção */}
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Selecionar Exercício"
        className="!p-0 flex h-[90dvh] max-h-[800px] w-full max-w-full flex-col sm:h-auto sm:max-w-md"
      >
        <div className="flex min-h-0 flex-col">
          {/* Header com busca - fixo no topo */}
          <div className="shrink-0 border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
            <div className="relative">
              <div className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nome..."
                autoFocus
                className="w-full rounded-lg border border-gray-300 bg-gray-50 py-3 pl-11 pr-4 text-sm font-medium outline-none transition-all placeholder:text-gray-400 focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-primary dark:focus:bg-gray-800 sm:text-base"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-gray-300"
                  aria-label="Limpar busca"
                >
                  <FiX className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Contador de resultados */}
            {!loading && results.length > 0 && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {results.length}{" "}
                {results.length === 1
                  ? "exercício encontrado"
                  : "exercícios encontrados"}
              </p>
            )}
          </div>

          {/* Lista de exercícios - área scrollável */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-white dark:bg-gray-800">
            {renderListContent()}
          </div>
        </div>
      </Modal>
    </div>
  );
};
